import React from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Box,
  Chip,
  Tooltip,
  Avatar,
  AvatarGroup,
  IconButton,
  alpha,
} from '@mui/material';
import {
  Play,
  MoreVertical,
  Clock,
  Calendar,
  CheckCircle,
  ArrowUpRight,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  FileText,
  Link2,
  Lock,
  GitMerge,
  Layers,
} from 'lucide-react';
import dayjs from 'dayjs';

import { Task, TaskStatus } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import StatusBadge from '@/shared/components/StatusBadge';
import {
  TaskTableColumn,
  TaskContextType,
  TaskTablePermissions,
} from './types';
import {
  ISSUE_TYPE_CONFIG,
  CATEGORY_STYLES,
  getCategoryStyle,
  getDeadlineStyle,
  TIMER_ACTIVE_BG,
  BLOCKED_TASK_BG,
} from './constants';
import { getUserRaciChip, getTaskLinkCounts } from './utils';
import { User } from './permissions';

export interface TaskTableRowProps {
  task: Task;
  columns: TaskTableColumn[];
  context: TaskContextType;
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed';
  isTimerActive: boolean;
  isPickedForToday: boolean;
  showProjectName: boolean;
  parentTask?: Task;
  user: User | null;
  permissions: TaskTablePermissions;
  onTaskClick: (taskId: string) => void;
  onCheckboxChange: (task: Task) => void;
  onStartTimer: (taskId: string, taskTitle: string) => void;
  onMenuAction: (action: string, task: Task, event?: React.MouseEvent<HTMLElement>) => void;
}

/**
 * TaskTableRow - Renders a single task row in the desktop table
 */
const TaskTableRow: React.FC<TaskTableRowProps> = ({
  task,
  columns,
  context,
  currentRaciRole,
  isTimerActive,
  isPickedForToday,
  showProjectName,
  parentTask,
  user,
  permissions,
  onTaskClick,
  onCheckboxChange,
  onStartTimer,
  onMenuAction,
}) => {
  const linkCounts = getTaskLinkCounts(task);
  const deadlineStyle = getDeadlineStyle(task.dueDate, task.status);
  const raciChipInfo = user ? getUserRaciChip(task, user, currentRaciRole) : null;

  // Determine row background
  const hasBlockers = (task.blockedByTaskIds?.length || 0) > 0;
  const rowBg = isTimerActive ? TIMER_ACTIVE_BG : hasBlockers ? BLOCKED_TASK_BG : 'inherit';

  /**
   * Render individual cell based on column type
   */
  const renderCell = (column: TaskTableColumn) => {
    switch (column) {
      case TaskTableColumn.CHECKBOX:
        return (
          <TableCell padding="checkbox">
            <Checkbox
              checked={task.status === TaskStatus.COMPLETED}
              onChange={(e) => {
                e.stopPropagation();
                onCheckboxChange(task);
              }}
              disabled={!permissions.canToggleCheckbox}
            />
          </TableCell>
        );

      case TaskTableColumn.TASK:
        return (
          <TableCell>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  textDecoration:
                    task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                }}
              >
                {task.title}
              </Typography>

              {/* Parent Task Link */}
              {parentTask && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTaskClick(parentTask.id);
                  }}
                >
                  <ArrowUpRight size={12} style={{ color: '#6366f1' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Subtask of: {parentTask.title}
                  </Typography>
                </Box>
              )}

              {/* Project Name (if enabled) */}
              {showProjectName && (task.projectName || task.projectId) && (
                <Typography variant="caption" color="text.secondary">
                  Project: {task.projectName || task.projectId}
                </Typography>
              )}
            </Box>
          </TableCell>
        );

      case TaskTableColumn.CATEGORY:
        return (
          <TableCell>
            {task.category ? (() => {
              const style = getCategoryStyle(task.category) ?? CATEGORY_STYLES[task.category];
              return style ? (
              <Chip
                size="small"
                label={style.label}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: style.bgcolor,
                  color: style.color,
                  fontWeight: 500,
                }}
              />
              ) : (
              <Chip size="small" label={task.category} />
              );
            })() : (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )}
          </TableCell>
        );

      case TaskTableColumn.ISSUE_TYPE:
        return (
          <TableCell>
            {task.issueType && ISSUE_TYPE_CONFIG[task.issueType] ? (
              <Tooltip title={ISSUE_TYPE_CONFIG[task.issueType].label}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: alpha(ISSUE_TYPE_CONFIG[task.issueType].color, 0.1),
                  }}
                >
                  {React.createElement(ISSUE_TYPE_CONFIG[task.issueType].icon, {
                    size: 14,
                    style: { color: ISSUE_TYPE_CONFIG[task.issueType].color },
                  })}
                </Box>
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )}
          </TableCell>
        );

      case TaskTableColumn.PRIORITY:
        return (
          <TableCell>
            <PriorityBadge priority={task.priority} size="small" />
          </TableCell>
        );

      case TaskTableColumn.STATUS:
        return (
          <TableCell>
            <StatusBadge status={task.status} size="small" />
          </TableCell>
        );

      case TaskTableColumn.LINKS:
        return (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
              {linkCounts.subtasks > 0 && (
                <Tooltip title={`${linkCounts.subtasks} subtask${linkCounts.subtasks > 1 ? 's' : ''}`}>
                  <Chip
                    icon={<Layers size={10} />}
                    label={linkCounts.subtasks}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: alpha('#8b5cf6', 0.1),
                      color: '#8b5cf6',
                      '& .MuiChip-icon': { ml: 0.5 },
                    }}
                  />
                </Tooltip>
              )}
              {linkCounts.blockedBy > 0 && (
                <Tooltip title={`Blocked by ${linkCounts.blockedBy} task${linkCounts.blockedBy > 1 ? 's' : ''}`}>
                  <Lock size={14} style={{ color: '#f59e0b' }} />
                </Tooltip>
              )}
              {linkCounts.blocking > 0 && (
                <Tooltip title={`Blocking ${linkCounts.blocking} task${linkCounts.blocking > 1 ? 's' : ''}`}>
                  <GitMerge size={14} style={{ color: '#6366f1' }} />
                </Tooltip>
              )}
              {linkCounts.hasAnyLinks && (
                <Tooltip title="Has dependencies">
                  <Link2 size={12} style={{ color: '#64748b' }} />
                </Tooltip>
              )}
              {!linkCounts.hasAnyLinks && (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
          </TableCell>
        );

      case TaskTableColumn.CREATED_AT:
        return (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Calendar size={14} style={{ color: '#94a3b8' }} />
              <Typography variant="body2" color="text.secondary">
                {dayjs(task.createdAt).format('MMM D, YYYY')}
              </Typography>
            </Box>
          </TableCell>
        );

      case TaskTableColumn.START_DATE:
        return (
          <TableCell>
            {task.startDate ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Calendar size={14} style={{ color: '#94a3b8' }} />
                <Typography variant="body2" color="text.secondary">
                  {dayjs(task.startDate).format('MMM D')}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )}
          </TableCell>
        );

      case TaskTableColumn.DUE_DATE:
        return (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Clock size={14} style={{ color: '#94a3b8' }} />
              <Typography variant="body2" sx={deadlineStyle}>
                {dayjs(task.dueDate).format('MMM D, YYYY')}
              </Typography>
            </Box>
          </TableCell>
        );

      case TaskTableColumn.COMPLETED_AT:
        return (
          <TableCell>
            {task.status === TaskStatus.COMPLETED && task.completedAt ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircle size={14} style={{ color: '#16a34a' }} />
                <Typography variant="body2" color="success.main">
                  {dayjs(task.completedAt).format('MMM D, YYYY h:mm A')}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )}
          </TableCell>
        );

      case TaskTableColumn.RACI:
        return (
          <TableCell>
            {raciChipInfo ? (
              <Tooltip 
                title={raciChipInfo.hasMultipleRoles 
                  ? `Roles: ${raciChipInfo.allRoles.join(', ')}` 
                  : raciChipInfo.label
                }
              >
                <Chip
                  label={raciChipInfo.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: raciChipInfo.bgColor,
                    color: raciChipInfo.color,
                    border: raciChipInfo.hasMultipleRoles ? `2px solid ${raciChipInfo.color}` : 'none',
                  }}
                />
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )}
          </TableCell>
        );

      case TaskTableColumn.ASSIGNED:
        return (
          <TableCell>
            <AvatarGroup max={2} sx={{ justifyContent: 'flex-start' }}>
              {task.raci?.responsible?.map((userId, idx) => {
                const displayName = task.raciNames?.responsible?.[idx] || userId;
                return (
                  <Tooltip key={userId} title={displayName}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: '0.75rem',
                        bgcolor: 'primary.main',
                      }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                );
              })}
            </AvatarGroup>
          </TableCell>
        );

      case TaskTableColumn.ACTIONS:
        return (
          <TableCell align="right">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
              {/* Timer Button */}
              {task.status !== TaskStatus.COMPLETED && permissions.canUseTimer && (
                <Tooltip
                  title={
                    isTimerActive
                      ? 'Timer active'
                      : isPickedForToday
                        ? 'Start timer'
                        : "Pick in Today's Tasks first"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isTimerActive && isPickedForToday) {
                          onStartTimer(task.id, task.title);
                        }
                      }}
                      disabled={isTimerActive || !isPickedForToday}
                    >
                    <Play
                      size={14}
                      fill={isTimerActive ? '#16a34a' : '#2563eb'}
                      style={{ color: isTimerActive ? '#16a34a' : '#2563eb' }}
                    />
                  </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* More Menu */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMenuAction('menu', task, e);
                }}
              >
                <MoreVertical size={16} />
              </IconButton>
            </Box>
          </TableCell>
        );

      default:
        return <TableCell>—</TableCell>;
    }
  };

  return (
    <TableRow
      hover
      onClick={() => onTaskClick(task.id)}
      sx={{
        cursor: 'pointer',
        bgcolor: rowBg,
      }}
    >
      {columns.map((column) => (
        <React.Fragment key={column}>{renderCell(column)}</React.Fragment>
      ))}
    </TableRow>
  );
};

export default TaskTableRow;
